<template>
    <label class="hc-header__search">
        <img
            class="ico-a-centrum-glass hc-header__search-icon"
            alt=""
            src="/frontend/web/img/help-center/centrum-glass.svg"
            @click="selectCurrentSelection()"
        />
        <!-- https://alphagov.github.io/accessible-autocomplete/examples/ -->
        <input
            type="text"
            role="combobox"
            :aria-activedescendant="
                isListVisible && currentSelectionIndex ? `autocomplete-default__option--${currentSelectionIndex}` : null
            "
            :aria-expanded="isListVisible ? true : false"
            aria-owns="autocomplete-default__listbox"
            aria-autocomplete="list"
            autocomplete="off"
            class="hc-header__search-input form-control"
            ref="inputRef"
            :placeholder="placeholder"
            v-model="searchText"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.enter.prevent="selectCurrentSelection"
        />
        <ul v-if="isListVisible" class="autocomplete__list" id="autocomplete-default__listbox" role="listbox">
            <li
                v-for="(item, index) in items"
                role="option"
                :tabindex="currentSelectionIndex === index ? null : -1"
                :aria-selected="currentSelectionIndex === index ? true : false"
                :aria-posinset="index"
                :aria-setsize="items.length"
                :id="`autocomplete-default__option--${index}`"
                class="autocomplete__list-item"
                :class="{ 'autocomplete__list-item--active': currentSelectionIndex === index }"
                :key="index"
                @mousedown.prevent
                @click="selectItem(item)"
                @mouseenter="currentSelectionIndex = index"
            >
                <span
                    class="autocomplete__list-item-text"
                    :data-text="transformationText(item)"
                    v-html="transformationHtml(item)"
                ></span>
            </li>
        </ul>
    </label>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Loader } from "@sazka/design-system/src/loader/loader";
// eslint-disable-next-line vue/no-dupe-keys
import { debounce } from "@sazka/utils/debounce";
import { UrlUtils } from "@sazka/utils/url";
import { SearchAnalytics } from "@/shared/utils/analytics/analytics-search";
import { useHcSearch } from "../composables/use-hc-search";
import { ArticlePreviewDto } from "../model/article";

const props = withDefaults(
    defineProps<{
        minInputLength: number;
        debounce: number;
        placeholder: string;
        enableResultPage: boolean;
    }>(),
    {
        minInputLength: 2,
        debounce: 500,
        enableResultPage: true,
    }
);

const { fetchResults, hideSearchResults, fetchAutocomplete } = useHcSearch();

const inputRef = ref<HTMLInputElement>(null);
const items = ref<ArticlePreviewDto[]>([]);
const isInputFocused = ref(false);
const searchText = ref("");
const currentSelectionIndex = ref(null);
const isListVisible = computed(
    () => isInputFocused.value && searchText.value.length >= props.minInputLength && items.value.length
);
const currentSelection = computed(() =>
    isListVisible.value && currentSelectionIndex.value < items.value.length
        ? items.value[currentSelectionIndex.value]
        : null
);

const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlightTextMatch = (text: string) => {
    const regexp = new RegExp(`(${escapeRegExp(searchText.value)})`, "ig");
    return text.replace(regexp, "<strong>$1</strong>");
};

const transformationHtml = (item: ArticlePreviewDto) =>
    `<a href="${item.pageUrl.relative}">${highlightTextMatch(item.name)}</a>`;
const transformationText = (item: ArticlePreviewDto) => item.name;

const fetch = async () => {
    if (searchText.value && searchText.value.length >= props.minInputLength) {
        items.value = await fetchAutocomplete(searchText.value);
    } else {
        items.value = [];
    }
    SearchAnalytics.pushSearchSuggester("help center", searchText.value, items.value.length);
};

const selectItem = (item: ArticlePreviewDto) => {
    SearchAnalytics.pushSuggesterClick("centrum podpory", currentSelection.value.name);
    searchText.value = transformationText(item);
    window.location.href = currentSelection.value.pageUrl.relative;
    Loader.show();
};

const onInput = () => {
    currentSelectionIndex.value = null;
};

const onFocus = () => {
    isInputFocused.value = true;
};

const onBlur = () => {
    isInputFocused.value = false;
};

const onArrowDown = () => {
    if (!isListVisible.value) return;
    if (currentSelectionIndex.value === null) currentSelectionIndex.value = 0;
    else if (currentSelectionIndex.value < items.value.length - 1) currentSelectionIndex.value++;
};

const onArrowUp = () => {
    if (!isListVisible.value) return;
    if (currentSelectionIndex.value === null) currentSelectionIndex.value = items.value.length - 1;
    else if (currentSelectionIndex.value > 0) currentSelectionIndex.value--;
    else currentSelectionIndex.value = null;
};

const onEscapeDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        isInputFocused.value = false;
    }
};

const selectCurrentSelection = async () => {
    if (currentSelection.value) {
        selectItem(currentSelection.value);
        hideSearchResults();
    } else if (props.enableResultPage) {
        try {
            Loader.show();
            await fetchResults(searchText.value);
            inputRef.value.blur();
        } finally {
            Loader.hide();
        }
    } else {
        Loader.show();
        window.location.href = `/centrum-podpory?search-text=${searchText.value}`;
    }
};

const loadLinksDebounced = debounce(() => fetch(), props.debounce);
watch(searchText, loadLinksDebounced);

onMounted(() => {
    document.addEventListener("keydown", (e) => onEscapeDown(e));
    if (props.enableResultPage) {
        const query = UrlUtils.getQueryString("search-text");
        if (query) {
            searchText.value = query;
            selectCurrentSelection();
        }
    }
});

onUnmounted(() => {
    document.removeEventListener("keydown", (e) => onEscapeDown(e));
});
</script>

<style lang="scss">
.hc-header__search {
    position: relative;
}

.autocomplete__list {
    position: absolute;
    top: calc(100% + 4px);
    box-shadow: 0px 10px 24px -6px rgba(0, 0, 0, 0.12);
    padding: $sp-dynamic-100;
    background-color: #fff;
    left: 0;
    right: 0;
    border-radius: $radius-xxs;
    list-style: none;
}

.autocomplete__list-item {
    a {
        text-decoration: none;
        color: $clr-gray-900;
    }

    &--active {
        text-decoration: underline;

        a {
            color: $clr-sazka-blue-900;
        }
    }
}
</style>
